import { useMemo } from 'react';

import type { ChainAddressId } from '@infinex/asset-config';

import type {
  BalancesByChainAddressId,
  ChainAssetBalanceSummed,
} from '../../../features/balances';
import { SMALL_BALANCE_THRESHOLD_USD } from '../../../lib';

/**
 * Hook that takes in a balancesByChainAddressId object and returns a sorted array of ChainAddressIds
 * based on the usd value of the balance.
 *
 * @returns
 */
export const useUsdSortedChainAssetBalances = (
  balancesByChainAddressId: BalancesByChainAddressId,
  excludeSmallBalances = false
): ChainAddressId[] => {
  const sortedBalances = useMemo(() => {
    // Create array of tuples with [chainAddressId, balance, usdValue]
    return sortBalanceByUsd(balancesByChainAddressId, excludeSmallBalances);
  }, [balancesByChainAddressId, excludeSmallBalances]);

  return sortedBalances as ChainAddressId[];
};

function sortBalanceByUsd(
  balancesByChainAddressId: BalancesByChainAddressId,
  excludeSmallBalances: boolean
) {
  const balances = Object.entries(balancesByChainAddressId).reduce<
    [ChainAddressId, ChainAssetBalanceSummed, number][]
  >((acc, [chainAddressId, balance]) => {
    if (!balance) return acc;

    const usdValue =
      balancesByChainAddressId?.[chainAddressId as ChainAddressId]?.balanceUsd;

    // Filter small balances during reduction. Tokens we can't value (unpriced
    // or zero USD — typically unverified dust) are treated as below the
    // threshold too, otherwise they slip through with no balance shown. These
    // lists are all "your held tokens" sections; destination/discovery tokens
    // come from a separate GroupedTokenList, so this never hides pickable
    // zero-balance assets.
    if (
      excludeSmallBalances &&
      (usdValue ?? 0) <= SMALL_BALANCE_THRESHOLD_USD
    ) {
      return acc;
    }

    acc.push([chainAddressId as ChainAddressId, balance, usdValue ?? 0]);
    return acc;
  }, []);

  // Sort the pre-calculated values
  return balances
    .sort((a, b) => b[2] - a[2])
    .map(([chainAddressId]) => chainAddressId);
}
